import {
  useMutation,
  useMutationState,
  useQueryClient,
  type MutationKey,
} from '@tanstack/react-query';
import fileDownload from 'js-file-download';
import { toast } from '../components/toast/Toast';
import type { RaptorFileDownloadButtonStatus } from '../components/feedback/RaptorFileDownloadButton.component';

const DEFAULT_EXPORT_STATUS_RESET_MS = 3000;

export type FileExportVariables = {
  positionDate: string;
  raptorPlatformId?: string;
};

export type UseFileExportMutationOptions = {
  mutationKey: MutationKey;
  download: (variables: FileExportVariables) => Promise<Blob>;
  getFileName: (positionDate: string) => string;
  errorMessage?: string;
  statusResetMs?: number;
};

const toDownloadButtonStatus = (
  status: string,
): RaptorFileDownloadButtonStatus => {
  if (status === 'pending') return 'loading';
  if (status === 'error') return 'error';
  if (status === 'success') return 'success';
  return 'idle';
};

/**
 * In-page file download mutation with status derived from `useMutationState`.
 * Completed mutation records are removed after a short timeout so the button
 * returns to idle. Pass a `mutationKey` that includes any values that should
 * reset status (e.g. position date, platform id).
 */
export const useFileExportMutation = ({
  mutationKey,
  download,
  getFileName,
  errorMessage = 'Export failed. Please try again.',
  statusResetMs = DEFAULT_EXPORT_STATUS_RESET_MS,
}: UseFileExportMutationOptions) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationKey,
    mutationFn: download,
    onSuccess: (blob, { positionDate: date }) => {
      fileDownload(blob, getFileName(date));
    },
    onError: () => {
      toast({
        title: 'Download failed',
        message: errorMessage,
        type: 'error',
        duration: 5000,
      });
    },
    onSettled: (_data, _error, variables) => {
      const completedMutation = queryClient
        .getMutationCache()
        .findAll({ mutationKey, exact: true })
        .find((entry) => entry.state.variables === variables);

      if (completedMutation) {
        window.setTimeout(() => {
          queryClient.getMutationCache().remove(completedMutation);
        }, statusResetMs);
      }
    },
  });

  const exportMutationStatuses = useMutationState({
    filters: {
      mutationKey,
      exact: true,
    },
    select: (entry) => entry.state.status,
  });
  const latestStatus =
    exportMutationStatuses[exportMutationStatuses.length - 1] ?? 'idle';

  return {
    mutate: mutation.mutate,
    isPending: latestStatus === 'pending',
    status: toDownloadButtonStatus(latestStatus),
  };
};
